import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import { Text } from "@volca/ui/components/ui/text";
import { SETTINGS_PAGE_TITLES } from "@/business-settings/constants";
import {
  useGetAchAccount,
  useGetAccountSummary,
} from "@/internal-transactions/query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner, isAdminOrOwnerOrManager } from "@/user/utils";
import AutoRefillBalanceModal from "./auto-refill-balance-modal";
import DepositWithdrawModal from "./deposit-withdraw-modal";
import { EmptyPaymentState } from "./empty-payment-state";
import { NewBankDetailsModal } from "./new-bank-details-modal";
import { PaymentsActions } from "./payments-actions";
import PaymentsSummary from "./payments-summary";
import PaymentsTable from "./payments-table";

const Payments = () => {
  const { businessId } = useParams();
  const [depositModalMode, setDepositModalMode] = useState<
    "deposit" | "withdraw" | undefined
  >(undefined);
  const [newBankDetailsModalOpen, setNewBankDetailsModalOpen] = useState(false);
  const [autoRefillBalanceModalOpen, setAutoRefillBalanceModalOpen] =
    useState(false);

  const { data: userInfo } = useGetMyUser();
  const { data: achAccount, isLoading: isAchAccountLoading } =
    useGetAchAccount(businessId);
  const { data: accountSummary } = useGetAccountSummary(businessId);

  const showPayments = isAdminOrOwnerOrManager(userInfo?.role);
  const canActOnPayments = isAdminOrOwner(userInfo?.role);
  const hasAutoRefill = accountSummary?.settings?.auto_refill_active;

  if (!showPayments) {
    return <Navigate to={`/business-settings/${businessId}/`} replace />;
  }

  const hasAchAccount = !!achAccount;

  return (
    <>
      <PageContainer variant="workspace">
        <PageHeader
          title={SETTINGS_PAGE_TITLES.payments}
          actions={
            canActOnPayments && (
              <PaymentsActions
                businessId={businessId}
                onDeposit={() => setDepositModalMode("deposit")}
                onWithdraw={() => setDepositModalMode("withdraw")}
                onAutoRefillBalance={() => setAutoRefillBalanceModalOpen(true)}
                onEditBankDetails={() => setNewBankDetailsModalOpen(true)}
              />
            )
          }
        />

        {hasAutoRefill && (
          <Alert color="information" role="status" className="items-center p-2">
            <InfoIcon />
            <AlertDescription>
              <Text weight="medium" className="text-sm">
                Your account is set up for auto-refill. You can edit this in
                your{" "}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 underline"
                  onClick={() => setAutoRefillBalanceModalOpen(true)}
                >
                  auto-refill settings
                </Button>
                .
              </Text>
            </AlertDescription>
          </Alert>
        )}

        <PageMainCard>
          <PageMainStack className="min-h-0 flex-1 pt-6">
            {!isAchAccountLoading && !hasAchAccount ? (
              <EmptyPaymentState
                className="mr-6 w-auto"
                onAddBankDetails={() => setNewBankDetailsModalOpen(true)}
              />
            ) : (
              <>
                <PaymentsSummary
                  businessId={businessId}
                  onAutoRefillClick={() => setAutoRefillBalanceModalOpen(true)}
                />
                <PaymentsTable />
              </>
            )}
          </PageMainStack>
        </PageMainCard>
      </PageContainer>
      <DepositWithdrawModal
        mode={depositModalMode}
        onOpenChange={(open) =>
          setDepositModalMode(open ? "deposit" : undefined)
        }
        businessId={businessId}
      />
      <NewBankDetailsModal
        isOpen={newBankDetailsModalOpen}
        onOpenChange={setNewBankDetailsModalOpen}
        businessId={businessId}
      />
      <AutoRefillBalanceModal
        isOpen={autoRefillBalanceModalOpen}
        onOpenChange={setAutoRefillBalanceModalOpen}
        businessId={businessId}
        currentSettings={accountSummary?.settings}
      />
    </>
  );
};

export default Payments;
